import { Link, useRouterState } from "@tanstack/react-router";
import { useGetV1Home } from "@solarkey/api-client";

import { useCapabilities } from "../session/capabilities";

/**
 * Fleet freshness chip (UX-002 §2.1) — shows "Fleet live", "N stale", or
 * "N path down" based on the home endpoint's data-path counts.
 * Sits in the TopBar. Clicking navigates to the fleet Data page (scope level)
 * or the site Data path page (site level) per wireframe mk 5.
 */
export function FreshnessChip() {
  // UX-REV-003 I1: this chip only needs the (period-independent) data_path, but it
  // calls /v1/home. Calling it bare keyed differently from Home's own
  // `{period:"today"}` call, so the Home page fired that ~7s endpoint TWICE. Use
  // the same default period so React Query serves one shared fetch on Home (the
  // undefined from/to are dropped from the key hash, so the keys match).
  const home = useGetV1Home({ period: "today" });
  const caps = useCapabilities();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const dp = home.data?.data?.data?.data_path;

  if (!dp) return null;

  // `sites_down` absent means "not checked", NOT "none down". Reading it as 0 let
  // this shell-wide chip claim a healthy data path on an unmeasured one, on every
  // screen at once (QA, 25 Oct). With the down-count unknown the chip makes no
  // claim at all rather than a reassuring one.
  const downKnown = dp.sites_down != null;
  const down = dp.sites_down?.length ?? 0;
  const up = dp.sites_up ?? 0;
  const total = up + down;

  if (total === 0 || !downKnown) return null;

  const state: "live" | "stale" | "path_down" =
    down > 0 ? "path_down" : "live";

  const m = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  const tenant = m?.[1] || caps.scopes[0]?.id || "";
  const scope = m?.[2] || caps.scopes[0]?.id || "";
  const siteMatch = pathname.match(/\/plants\/([^/]+)/);
  const site = siteMatch?.[1];

  const href = site
    ? `/t/${tenant}/s/${scope}/plants/${site}/datapath`
    : `/t/${tenant}/s/${scope}/data`;

  return (
    <Link to={href as never} style={chipStyle} data-freshness={state} aria-label={ariaLabel(state, up, down)}>
      <span style={{ ...dot, background: DOT_COLOR[state] }} aria-hidden />
      <span>{chipLabel(state, up, down)}</span>
    </Link>
  );
}

function chipLabel(state: string, up: number, down: number): string {
  if (state === "path_down") return `${down} path down`;
  if (state === "stale") return "Fleet stale";
  return `Fleet live · ${up}`;
}

function ariaLabel(state: string, up: number, down: number): string {
  if (state === "path_down") return `${down} of ${up + down} sites have path down`;
  return `Fleet live, ${up} sites reporting`;
}

const DOT_COLOR: Record<string, string> = {
  live: "var(--sk-color-health-ok)",
  stale: "var(--sk-color-health-warning)",
  path_down: "var(--sk-color-health-fault)",
};

const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "2px 8px",
  borderRadius: "var(--sk-radius-control)",
  border: "1px solid var(--sk-color-border-1)",
  font: "500 11px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-2)",
  whiteSpace: "nowrap",
  textDecoration: "none",
  cursor: "pointer",
};

const dot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
};
